import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ChevronRight, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getPublicFooter } from "../api/footer";
import apiClient from "../api/axios";

export const PublicFooter: React.FC = () => {
    const { data: footer, isLoading } = useQuery({
        queryKey: ["publicFooter"],
        queryFn: getPublicFooter,
    });

    const { data: affiliations } = useQuery({
        queryKey: ['publicAffiliations'],
        queryFn: async () => {
            const response = await apiClient.get('/public/affiliations');
            return response.data.data;
        }
    });

    if (isLoading || !footer) return null;

    const socialLinks = [
        { icon: <Facebook className="h-5 w-5" />, url: footer.facebookUrl, label: "Facebook" },
        { icon: <Instagram className="h-5 w-5" />, url: footer.instagramUrl, label: "Instagram" },
        { icon: <Twitter className="h-5 w-5" />, url: footer.twitterUrl, label: "Twitter" },
        { icon: <Youtube className="h-5 w-5" />, url: footer.youtubeUrl, label: "Youtube" },
    ].filter(link => link.url);

    return (
        <footer className="border-t border-border bg-secondary pb-8 pt-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* First Row: 3 Columns */}
                <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
                    
                    {/* Column 1: Logo & Subtitle */}
                    <div className="flex flex-col gap-6">
                        <Link to="/" className="flex items-center gap-3">
                            {footer.logoUrl ? (
                                <img src={footer.logoUrl} alt="Mosaik Logo" className="h-12 w-auto" />
                            ) : (
                                <span className="text-2xl font-bold text-primary">Mosaik</span>
                            )}
                        </Link>
                        <p className="text-sm text-secondary-foreground/80 leading-relaxed max-w-xs">
                            {footer.logoSubtitle}
                        </p>
                        {socialLinks.length > 0 && (
                            <div className="flex items-center gap-4">
                                {socialLinks.map((social, id) => (
                                    <a 
                                        key={id} 
                                        href={social.url!} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all"
                                        aria-label={social.label}
                                    >
                                        {social.icon}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Column 2: Kontakt */}
                    <div>
                        <h3 className="text-lg font-bold text-foreground mb-6">{footer.contactTitle}</h3>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3 text-sm text-secondary-foreground/80">
                                <MapPin className="h-5 w-5 shrink-0 text-primary" />
                                <span>{footer.address}</span>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                                <Phone className="h-5 w-5 shrink-0 text-primary" />
                                <span>{footer.phone}</span>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                                <Mail className="h-5 w-5 shrink-0 text-primary" />
                                <a href={`mailto:${footer.email}`} className="hover:text-primary transition-colors">
                                    {footer.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-sm text-secondary-foreground/80 pt-2 border-t border-border/50">
                                <Clock className="h-5 w-5 shrink-0 text-primary" />
                                <div className="flex flex-col gap-1">
                                    <span>{footer.openingHours}</span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3: Info */}
                    <div>
                        <h3 className="text-lg font-bold text-foreground mb-6">{footer.infoTitle}</h3>
                        <ul className="space-y-3 text-sm text-secondary-foreground/80">
                            {footer.links?.map((link: any, index: number) => (
                                <li key={index}>
                                    <Link to={link.url} className="inline-flex items-center gap-2 hover:text-primary transition-colors group">
                                        <ChevronRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform" />
                                        {link.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Affiliations/Memberships Area */}
                {affiliations && affiliations.length > 0 && (
                    <div className="mt-16 border-t border-border/50 pt-10 text-center">
                        <p className="text-sm font-semibold text-secondary-foreground/60 mb-6 tracking-wider uppercase">Mitgliedschaften</p>
                        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-80 hover:opacity-100 transition-opacity">
                            {affiliations.map((aff: any) => {
                                const El = aff.linkUrl ? "a" : "div";
                                const props = aff.linkUrl ? { href: aff.linkUrl, target: "_blank", rel: "noopener noreferrer" } : {};
                                return (
                                    <El key={aff.id} {...props as any} className="flex h-12 md:h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300">
                                        <img src={aff.logoUrl} alt={aff.name || "Mitgliedschaft"} className="max-h-full max-w-full object-contain" />
                                    </El>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Second Row: Copyright */}
                <div className="mt-12 border-t border-border/50 pt-8 text-center bg-black/5 rounded-2xl mb-8 p-4">
                    <p className="text-xs text-secondary-foreground/50">
                        {footer.copyrightText}
                    </p>
                </div>
            </div>
        </footer>
    );
};
